import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash, X } from "lucide-react";
import { useRef, useState } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useManualConversion } from "@/features/dashboard/queries/conversion.create.queries";
import { StudentInformationInput } from "@/types/dashboard.types";
import { toast } from "sonner";

interface Row {
  id: number;
  course: string;
  credits: string;
  grade: string;
}

// interface GradesTableRow {
//   subject_name: string;
//   credits?: string;
//   grade: string;
// }

// // New interface to match the actual data structure
// interface GradesTableData {
//   Course: string[];
//   Grade: string[];
//   "Credits/Max marks": (number | null)[];
// }

export function ManualGpaCalculator({
  countryId,
  gradingScaleId,
  pdfFile,
}: {
  countryId: string;
  gradingScaleId: string;
  pdfFile: File | null;
}) {
  const nextIdRef = useRef(6);
  const { mutateAsync: createManualConversion, isPending } =
    useManualConversion();
  const [rows, setRows] = useState<Row[]>(() => {
    const initialRows = Array.from({ length: 5 }, (_, i) => ({
      id: i + 1,
      course: "",
      credits: "",
      grade: "",
    }));
    return initialRows;
  });

  const [rowsToAdd, setRowsToAdd] = useState(1);
  const [studentInformation, setStudentInformation] =
    useState<StudentInformationInput>({
      name: "",
      grade: "",
    });

  const handleAddRows = () => {
    const newRows = Array.from({ length: rowsToAdd }, (_, i) => ({
      id: nextIdRef.current + i,
      course: "",
      credits: "",
      grade: "",
    }));

    nextIdRef.current += rowsToAdd;

    setRows([...rows, ...newRows]);
  };

  const handleDeleteRow = (id: number) => {
    if (rows.length <= 1) {
      toast.warning("Cannot delete the last row");
      return;
    }

    setRows(rows.filter((row) => row.id !== id));
  };

  const handleClearAll = () => {
    nextIdRef.current = 6;

    setRowsToAdd(1);

    setRows(
      Array.from({ length: 5 }, (_, i) => ({
        id: i + 1,
        course: "",
        credits: "",
        grade: "",
      }))
    );
    setStudentInformation({
      name: "",

      grade: "",
    });

    toast.success("All rows cleared");
  };

  const handleInputChange = (id: number, field: keyof Row, value: string) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    field: "course" | "credits" | "grade"
  ) => {
    const { key } = e;
    let targetElement: HTMLInputElement | null = null;

    if (key === "ArrowUp") {
      e.preventDefault();
      if (rowIndex > 0) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex - 1}'][data-field='${field}']`
        );
      }
    } else if (key === "ArrowDown") {
      e.preventDefault();
      if (rowIndex < rows.length - 1) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex + 1}'][data-field='${field}']`
        );
      }
    } else if (key === "ArrowLeft") {
      e.preventDefault();
      if (field === "grade") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='credits']`
        );
      } else if (field === "credits") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='course']`
        );
      } else if (field === "course" && rowIndex > 0) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex - 1}'][data-field='grade']`
        );
      }
    } else if (key === "ArrowRight") {
      e.preventDefault();
      if (field === "course") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='credits']`
        );
      } else if (field === "credits") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='grade']`
        );
      } else if (field === "grade" && rowIndex < rows.length - 1) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex + 1}'][data-field='course']`
        );
      }
    }

    if (targetElement) {
      targetElement.focus();
      targetElement.select();
    }
  };

  const handleCalculate = async () => {
    // Check student information validation
    const studentInfoValues = Object.values(studentInformation);
    const hasAnyStudentInfo = studentInfoValues.some((value) => value !== "");
    const hasIncompleteStudentInfo =
      hasAnyStudentInfo && studentInfoValues.some((value) => value === "");

    if (hasIncompleteStudentInfo) {
      toast.error(
        "Please complete all student information fields or leave them all empty"
      );
      return;
    }

    if (
      rows.some(
        (row) => row.course === "" || row.credits === "" || row.grade === ""
      )
    ) {
      toast.error("Please fill all the fields");
      return;
    }

    const payload = {
      data: rows,
      countryId,
      gradingScaleId,
      studentInformation,
      pdfFile,
    };

    if (!payload.pdfFile) {
      toast.warning("Please attach a transcript");
      return;
    }

    try {
      await createManualConversion(payload);
      toast.success("GPA calculated successfully");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-2">
      {/* Load Extracted Data Button */}
      {/* {hasExtractedData && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  AI-Extracted Data Available
                </p>
                <p className="text-xs text-blue-700">
                  We found grade data from your document that you can use as a
                  starting point
                </p>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      BETA
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadExtractedData}
                      disabled={isPending}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Load Extracted Data
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-sm">
                    <p className="font-medium">AI-Extracted Data</p>
                    <p className="text-xs mt-1">
                      This data was automatically extracted from your document.
                      It may not be 100% accurate, so please review and edit as
                      needed. This feature is in beta.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )} */}

      <div className="border max-h-[50vh] overflow-y-auto rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="divide-x hover:bg-muted/50">
              <TableHead className="text-center">#</TableHead>
              <TableHead>Course</TableHead>
              <TableHead className="w-20">Credits</TableHead>
              <TableHead className="w-22">Grade</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-border">
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                className="hover:bg-muted/50 divide-x divide-border"
              >
                <TableCell className="text-center p-0">{index + 1}</TableCell>
                <TableCell>
                  <Input
                    disabled={isPending}
                    value={row.course}
                    onChange={(e) =>
                      handleInputChange(row.id, "course", e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(e, index, "course")}
                    data-row-index={index}
                    data-field="course"
                    className="border-0 bg-transparent"
                  />
                </TableCell>
                <TableCell className="w-fit">
                  <Input
                    disabled={isPending}
                    value={row.credits}
                    onChange={(e) =>
                      handleInputChange(row.id, "credits", e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(e, index, "credits")}
                    data-row-index={index}
                    data-field="credits"
                    className="border-0 bg-transparent"
                    type="number"
                  />
                </TableCell>
                <TableCell className="w-fit">
                  <Input
                    disabled={isPending}
                    value={row.grade}
                    onChange={(e) =>
                      handleInputChange(row.id, "grade", e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(e, index, "grade")}
                    data-row-index={index}
                    data-field="grade"
                    className="border-0 bg-transparent"
                  />
                </TableCell>
                <TableCell className="text-center p-0">
                  <Button
                    variant="ghost"
                    disabled={isPending}
                    size="sm"
                    onClick={() => handleDeleteRow(row.id)}
                    className="size-4 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <span className="text-sm font-medium">Number of rows to add:</span>
        <div className="flex items-center">
          <Input
            type="number"
            min="1"
            value={rowsToAdd}
            disabled={isPending}
            onChange={(e) => setRowsToAdd(Number(e.target.value))}
            className="w-16"
          />
          <Button variant="ghost" onClick={handleAddRows} className="ml-2">
            Add
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <StudentInformationDialog
              studentInformation={studentInformation}
              setStudentInformation={setStudentInformation}
            />
          </div>
          <Button
            variant="destructive"
            onClick={handleClearAll}
            className="px-8"
            disabled={isPending}
          >
            <Trash />
          </Button>
        </div>

        <Button
          className="w-full px-8"
          onClick={handleCalculate}
          disabled={isPending}
        >
          {isPending ? "Calculating..." : "Calculate GPA"}
        </Button>
      </div>
    </div>
  );
}

export function StudentInformationDialog({
  studentInformation,
  setStudentInformation,
}: {
  studentInformation: StudentInformationInput;
  setStudentInformation: (studentInformation: StudentInformationInput) => void;
}) {
  const handleChange = (
    field: keyof StudentInformationInput,
    value: string
  ) => {
    setStudentInformation({ ...studentInformation, [field]: value });
  };

  const studentInfoValues = Object.values(studentInformation);
  const hasAnyStudentInfo = studentInfoValues.some(
    (value) => value.trim() !== ""
  );
  const allFieldsFilled = studentInfoValues.every(
    (value) => value.trim() !== ""
  );

  let buttonText = "Add Student Information";
  let indicator: React.ReactNode = null;

  if (hasAnyStudentInfo) {
    if (allFieldsFilled) {
      buttonText = "Edit Student Information";
      indicator = <div className="w-2 h-2 mr-2 rounded-full bg-green-500" />;
    } else {
      buttonText = "Student Information (Incomplete)";
      indicator = <div className="w-2 h-2 mr-2 rounded-full bg-yellow-500" />;
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          {indicator}
          <Pencil className="mr-2 h-4 w-4" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Student Information</DialogTitle>
          <DialogDescription>
            Please fill in the student information to calculate the GPA.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input
              placeholder="Enter name of the student"
              value={studentInformation.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Grade</Label>
            <Input
              placeholder="Enter grade of the student"
              value={studentInformation.grade}
              onChange={(e) => handleChange("grade", e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button>Save</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
